import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DollarSign, MapPin, Clock } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import {
  FORMULA_PRICING,
  LOCAL_SERVICE,
  SAT_AIRPORT_PICKUP,
  isLateNight as checkLateNight,
  checkFlatRate,
} from "@/lib/pricing-constants";

const LivePriceQuoteTool = () => {
  const { toast } = useToast();
  const [pickupAddress, setPickupAddress] = useState("");
  const [dropoffAddress, setDropoffAddress] = useState("");
  const [pickupTime, setPickupTime] = useState("");
  const [isCalculating, setIsCalculating] = useState(false);
  const [quoteResult, setQuoteResult] = useState<{
    price: number;
    distance: number;
    duration: string;
    method: string;
    gratuityIncluded?: boolean;
    lateNightFee?: number;
  } | null>(null);

  const calculateQuote = async () => {
    if (!pickupAddress.trim() || !dropoffAddress.trim()) {
      toast({
        title: "Missing Information",
        description: "Please enter both pickup and drop-off addresses.",
        variant: "destructive",
      });
      return;
    }

    // @ts-ignore
    if (typeof window.google === 'undefined' || !window.google.maps) {
      toast({
        title: "Maps Not Available",
        description: "Google Maps is not loaded. Please refresh the page.",
        variant: "destructive",
      });
      return;
    }

    setIsCalculating(true);
    setQuoteResult(null);

    try {
      // @ts-ignore
      const service = new window.google.maps.DistanceMatrixService();
      
      const request = {
        origins: [pickupAddress],
        destinations: [dropoffAddress],
        // @ts-ignore
        travelMode: window.google.maps.TravelMode.DRIVING,
        // @ts-ignore
        unitSystem: window.google.maps.UnitSystem.IMPERIAL,
      };

      // @ts-ignore
      service.getDistanceMatrix(request, (response: any, status: string) => {
        setIsCalculating(false);

        if (status === 'OK' && response) {
          const element = response.rows[0]?.elements[0];

          if (element && element.status === 'OK') {
            const distanceInMeters = element.distance?.value || 0;
            const distanceInMiles = distanceInMeters / 1609.34;
            const durationSeconds = element.duration?.value || 0;
            const durationMinutes = durationSeconds / 60;
            const durationText = element.duration?.text || 'N/A';

            const isLateNight = checkLateNight(pickupTime);
            const surcharge = isLateNight ? FORMULA_PRICING.LATE_NIGHT_SURCHARGE : 0;
            const destination = (pickupAddress + ' ' + dropoffAddress).toLowerCase();

            let totalPrice = 0;
            let method = '';
            let gratuityIncluded = false;

            // PRIORITY 1: Austin flat rate
            const flatRate = checkFlatRate(dropoffAddress, distanceInMiles) || checkFlatRate(pickupAddress, distanceInMiles);
            if (flatRate || destination.includes('austin') || destination.includes('bergstrom') || destination.includes('aus')) {
              const baseFare = 395;
              const sub = baseFare + surcharge;
              const grat = sub * FORMULA_PRICING.GRATUITY_RATE;
              totalPrice = sub + grat;
              method = 'Austin / AUS Airport Flat Rate';
            }
            // PRIORITY 2: SAT Airport Pickup (< 15 mi)
            else if (distanceInMiles < SAT_AIRPORT_PICKUP.DISTANCE_THRESHOLD && pickupAddress.toLowerCase().match(/\b(sat|san antonio airport|sat airport|sa airport|satx)\b/)) {
              totalPrice = SAT_AIRPORT_PICKUP.PRICE + surcharge;
              method = 'SAT Airport Pickup (< 15 mi)';
              gratuityIncluded = true;
            }
            // PRIORITY 3: Local Service (< 15 mi)
            else if (distanceInMiles < LOCAL_SERVICE.DISTANCE_THRESHOLD) {
              const sub = LOCAL_SERVICE.BASE_FARE + surcharge;
              const grat = sub * FORMULA_PRICING.GRATUITY_RATE;
              totalPrice = sub + grat;
              method = 'Local Service (< 15 mi)';
            }
            // PRIORITY 4: Distance & Time Formula (≥ 15 mi)
            else {
              const sub = FORMULA_PRICING.BASE_FEE + (distanceInMiles * FORMULA_PRICING.PER_MILE) + (durationMinutes * FORMULA_PRICING.PER_MINUTE) + surcharge;
              const grat = sub * FORMULA_PRICING.GRATUITY_RATE;
              totalPrice = sub + grat;
              method = 'Distance & Time Formula';
            }

            setQuoteResult({
              price: parseFloat(totalPrice.toFixed(2)),
              distance: parseFloat(distanceInMiles.toFixed(1)),
              duration: durationText,
              method,
              gratuityIncluded,
              lateNightFee: surcharge,
            });

            toast({
              title: "Quote Calculated",
              description: "Your estimated price is ready!",
            });
          } else {
            toast({
              title: "Route Not Found",
              description: "Unable to calculate route. Please check your addresses.",
              variant: "destructive",
            });
          }
        } else {
          toast({
            title: "Calculation Failed",
            description: "Unable to calculate distance. Please try again.",
            variant: "destructive",
          });
        }
      });
    } catch (error) {
      setIsCalculating(false);
      toast({
        title: "Error",
        description: "An error occurred while calculating your quote.",
        variant: "destructive",
      });
    }
  };

  return (
    <section className="py-20 bg-gradient-to-b from-background to-card/30">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12 space-y-4">
            <h2 className="text-4xl md:text-5xl font-serif font-bold text-foreground">
              Live Price Quote Tool
            </h2>
            <div className="w-24 h-1 bg-primary mx-auto" />
            <p className="text-lg text-muted-foreground">
              Get an instant price estimate for your ride
            </p>
          </div>

          <Card className="card-luxury border-primary/20 bg-card/90 backdrop-blur-xl">
            <CardContent className="p-8 md:p-12">
              <div className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="pickup-quote" className="text-primary font-semibold uppercase text-xs tracking-wider flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    Pickup Address
                  </Label>
                  <Input
                    id="pickup-quote"
                    type="text"
                    placeholder="e.g., 123 Main St, San Antonio, TX"
                    value={pickupAddress}
                    onChange={(e) => setPickupAddress(e.target.value)}
                    className="bg-background/50 border-border/50 focus:border-primary h-12"
                    maxLength={200}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="dropoff-quote" className="text-primary font-semibold uppercase text-xs tracking-wider flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    Drop-off Address
                  </Label>
                  <Input
                    id="dropoff-quote"
                    type="text"
                    placeholder="e.g., SAT Airport, San Antonio, TX"
                    value={dropoffAddress}
                    onChange={(e) => setDropoffAddress(e.target.value)}
                    className="bg-background/50 border-border/50 focus:border-primary h-12"
                    maxLength={200}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="pickup-time-quote" className="text-primary font-semibold uppercase text-xs tracking-wider flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    Pickup Time (Optional)
                  </Label>
                  <Input
                    id="pickup-time-quote"
                    type="time"
                    value={pickupTime}
                    onChange={(e) => setPickupTime(e.target.value)}
                    className="bg-background/50 border-border/50 focus:border-primary h-12"
                  />
                  <p className="text-xs text-muted-foreground">
                    Late night pickups (9 PM - 5 AM) include a $35 surcharge
                  </p>
                </div>

                <Button
                  onClick={calculateQuote}
                  className="w-full btn-luxury text-lg py-6 font-bold uppercase tracking-wider"
                  disabled={isCalculating}
                >
                  {isCalculating ? (
                    <>
                      <div className="w-5 h-5 border-2 border-background/30 border-t-background rounded-full animate-spin mr-2" />
                      Calculating...
                    </>
                  ) : (
                    <>
                      <DollarSign className="w-5 h-5 mr-2" />
                      Get Quote
                    </>
                  )}
                </Button>

                {quoteResult && (
                  <div className="mt-8 space-y-4 animate-in fade-in slide-in-from-bottom-4 duration-500">
                    <div className="bg-primary/10 border-2 border-primary/30 rounded-lg p-6 space-y-4">
                      <h3 className="text-2xl font-bold text-center text-primary">
                        Your Estimated Quote
                      </h3>

                      {/* Service type badge */}
                      <div className="text-center">
                        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-blue-500/20 text-blue-400">
                          {quoteResult.method}
                        </span>
                      </div>
                      
                      <div className="text-center">
                        <div className="text-5xl font-bold text-foreground mb-2">
                          ${quoteResult.price.toFixed(2)}
                        </div>
                        <div className="space-y-1">
                          {quoteResult.gratuityIncluded && (
                            <p className="text-sm text-emerald-500 font-medium">
                              ✓ 20% Gratuity Included
                            </p>
                          )}
                          {quoteResult.lateNightFee && quoteResult.lateNightFee > 0 && (
                            <p className="text-sm text-amber-500 font-medium">
                              + Late-Night/Early Morning Surcharge ($35)
                            </p>
                          )}
                          <p className="text-sm text-muted-foreground">
                            All prices include 20% gratuity
                          </p>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4 pt-4 border-t border-primary/20">
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <MapPin className="w-4 h-4 text-primary" />
                          <div>
                            <div className="text-xs uppercase tracking-wider text-primary font-semibold">Distance</div>
                            <div className="text-lg font-bold text-foreground">{quoteResult.distance} miles</div>
                          </div>
                        </div>

                        <div className="flex items-center gap-2 text-muted-foreground">
                          <Clock className="w-4 h-4 text-primary" />
                          <div>
                            <div className="text-xs uppercase tracking-wider text-primary font-semibold">Travel Time</div>
                            <div className="text-lg font-bold text-foreground">{quoteResult.duration}</div>
                          </div>
                        </div>
                      </div>

                      <div className="text-center pt-4 border-t border-primary/20">
                        <p className="text-xs text-muted-foreground">
                          * Final price may vary based on service type, time of day, and additional requirements.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default LivePriceQuoteTool;
